import { useEffect, useState } from "react";
import { Button } from "@a2z/ui";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

// The Trioty "Edit Product" floating control bar. On desktop: a centered pill
// that rises from the bottom when there are unsaved changes. On mobile: the
// design's full-width bottom bar — Preview (clean) / Discard (dirty) on the left
// and Saved (clean) / Save changes (dirty) on the right — always present.

type Props = {
  /** Aggregated count of dirty sections. Bar hidden when zero. */
  unsavedCount: number;
  /** Human-readable "Last edited X minutes ago" — pass null to hide. */
  lastEditedLabel: string | null;
  isSaving: boolean;
  onDiscard: () => void;
  onSaveChanges: () => void;
  /** Buyer storefront URL for the mobile "Preview" button (clean state). */
  previewHref?: string | null;
  /** Label override hooks for i18n. */
  labels?: {
    discard?: string;
    saveChanges?: string;
    unsavedSingular?: string;
    unsavedPlural?: string;
    preview?: string;
    saved?: string;
  };
};

export function ProductEditSaveBar({
  unsavedCount,
  lastEditedLabel,
  isSaving,
  onDiscard,
  onSaveChanges,
  previewHref,
  labels,
}: Props) {
  const isMobile = useIsMobile();
  const visible = unsavedCount > 0 || isSaving;

  // Mobile: the design's full-width bottom action bar, always visible.
  if (isMobile) {
    const dirty = unsavedCount > 0;
    const discardLabel = labels?.discard ?? "Discard";
    const saveChangesLabel = labels?.saveChanges ?? "Save changes";
    const previewLabel = labels?.preview ?? "Preview";
    const savedLabel = labels?.saved ?? "Saved";
    const ghost =
      "pointer-events-auto flex flex-1 items-center justify-center gap-2 rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--soft)] px-4 py-[14px] text-[14.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft2)] disabled:opacity-50";
    return (
      <div
        className="pointer-events-none fixed inset-x-0 z-50 flex items-center gap-2.5 px-4 pt-5 lg:hidden"
        style={{
          bottom: 0,
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          background:
            "linear-gradient(to top, var(--canvas) 62%, color-mix(in srgb, var(--canvas) 0%, transparent))",
        }}
      >
        {dirty ? (
          <button type="button" onClick={onDiscard} disabled={isSaving} className={ghost}>
            {discardLabel}
          </button>
        ) : previewHref ? (
          <a href={previewHref} target="_blank" rel="noreferrer" className={ghost}>
            <EyeIcon />
            {previewLabel}
          </a>
        ) : (
          <span className="flex-1" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={onSaveChanges}
          disabled={!dirty || isSaving}
          className="pointer-events-auto flex flex-[1.5] items-center justify-center gap-2 rounded-[15px] bg-[color:var(--accent-500)] px-4 py-[14px] text-[14.5px] font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90 disabled:opacity-55"
        >
          {dirty ? saveChangesLabel : savedLabel}
        </button>
      </div>
    );
  }

  // Rise-in transition: render hidden, then flip on a paint frame so the CSS
  // transition always plays. Reset the flag during render (not in an effect)
  // when the bar goes away, so the next appearance animates in fresh.
  const [entered, setEntered] = useState(false);
  const [wasVisible, setWasVisible] = useState(visible);
  if (visible !== wasVisible) {
    setWasVisible(visible);
    if (!visible) setEntered(false);
  }
  useEffect(() => {
    if (!visible) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [visible]);

  if (!visible) return null;

  const discardLabel = labels?.discard ?? "Discard";
  const saveChangesLabel = labels?.saveChanges ?? "Save changes";
  const unsavedLabel =
    unsavedCount === 1
      ? labels?.unsavedSingular ?? "1 unsaved change"
      : (labels?.unsavedPlural ?? "{n} unsaved changes").replace(
          "{n}",
          String(unsavedCount),
        );

  return (
    <div
      role="region"
      aria-label={saveChangesLabel}
      className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 flex justify-center px-4 lg:bottom-6"
    >
      <div
        className={`pointer-events-auto flex items-center gap-4 rounded-[15px] border border-[color:var(--border-light)] bg-[color:var(--surface)] py-[9px] pe-[9px] ps-[18px] shadow-[0_14px_38px_rgba(16,16,15,0.18),0_3px_10px_rgba(16,16,15,0.08)] transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${
          entered ? "translate-y-0 opacity-100" : "translate-y-3.5 opacity-0"
        }`}
      >
        <div className="flex items-center gap-[9px] text-[13px] font-semibold text-[color:var(--text-primary)]">
          <span
            aria-hidden="true"
            className="h-2 w-2 shrink-0 rounded-full bg-[#FACC15]"
          />
          <span className="whitespace-nowrap">{unsavedLabel}</span>
          {lastEditedLabel ? (
            <>
              <span className="text-[color:var(--text-muted)]" aria-hidden="true">
                ·
              </span>
              <span className="whitespace-nowrap font-normal text-[color:var(--text-muted)]">
                {lastEditedLabel}
              </span>
            </>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDiscard}
            disabled={isSaving}
          >
            {discardLabel}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onSaveChanges}
            disabled={isSaving}
            isLoading={isSaving}
          >
            <span className="whitespace-nowrap">{saveChangesLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
